import { Component, ElementRef, HostListener, Inject, Input } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';

import { ButtonTheme } from '@fcom/ui-components';
import { LoginStatus, LoginStep } from '@fcom/core-api/login';
import { loginStatus } from '@fcom/core/selectors';
import { AppState, ConfigService } from '@fcom/core';
import { addClass, removeClass } from '@fcom/core/utils';
import { finShare, keepState } from '@fcom/rx';
import { LoginActions } from '@fcom/core/actions';

import { JoinService, LoginService } from '../../services';

const STEP_CHANGE_TRANSITION_DURATION_MS = 500;

export enum LoginWidgetContext {
  PORTAL = 'portal',
  BOOKING = 'booking',
  MMB = 'mmb',
}

@Component({
  selector: 'fin-login-widget',
  templateUrl: './login-widget.component.html',
  styleUrls: ['./login-widget.component.scss'],
})
export class LoginWidgetComponent {
  readonly LoginStatus = LoginStatus;
  readonly ButtonTheme = ButtonTheme;
  readonly loginStatus$: Observable<LoginStatus>;
  readonly LoginWidgetContext = LoginWidgetContext;

  @Input()
  context: LoginWidgetContext = LoginWidgetContext.PORTAL;

  isOpen$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  widgetInDom$: Observable<boolean>;

  constructor(
    private store$: Store<AppState>,
    private elementRef: ElementRef,
    private loginService: LoginService,
    private joinService: JoinService,
    private configService: ConfigService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.loginStatus$ = this.store$.pipe(loginStatus(), finShare());
    this.widgetInDom$ = this.isOpen$.pipe(keepState(STEP_CHANGE_TRANSITION_DURATION_MS));
  }

  open(): void {
    this.isOpen$.next(true);
    addClass(this.document.body, 'overflow-hidden-tablet-down');
  }

  close(): void {
    this.isOpen$.next(false);
    removeClass(this.document.body, 'overflow-hidden-tablet-down');
  }

  redirectToLogin(): void {
    if (this.configService.cfg.enableLoginDialog) {
      this.store$.dispatch(LoginActions.setLoginPhaseStep({ step: LoginStep.CREDENTIALS }));
      this.close();
    } else {
      this.loginService.redirectToLogin();
    }
  }

  redirectToJoin(): void {
    if (this.configService.cfg.enableJoinDialog) {
      this.joinService.joinDialogOpen$.next(true);
      this.close();
    } else {
      this.loginService.redirectToJoin();
    }
  }

  @HostListener('document:click', ['$event'])
  clickout(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.close();
    }
  }
}
